import * as React from 'react';
import { Pencil } from 'lucide-react';
import { z } from 'zod';
import { Avatar, AvatarFallback, AvatarImage } from './Avatar';
import { Button } from './Button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from './Card';
import { FormLayout } from './Field';
import { Form, FormField, type ZodForm } from './Form';
import { IconTile } from './Icon';
import { Input } from './Input';
import { Grid, Stack } from './Layout';
import { Select } from './Select';

export const companyInfoSchema = z.object({
  name: z.string().trim().min(1, 'Company name is required'),
  supportEmail: z
    .string()
    .trim()
    .min(1, 'Support email is required')
    .email('Please enter a valid email address'),
  companyPhone: z.string(),
  streetAddress: z.string(),
  city: z.string(),
  /** A two-letter code from the state list, or empty. */
  state: z.string(),
  zipCode: z.string(),
});

export type CompanyInfoValues = z.infer<typeof companyInfoSchema>;
export type CompanyInfoForm = ZodForm<typeof companyInfoSchema>;

export interface CompanyInfoFormProps {
  /** From `useZodForm(companyInfoSchema, …)`. */
  form: CompanyInfoForm;
  onSubmit: (values: CompanyInfoValues) => void;
  /** Disables Save and relabels it while the save runs. */
  saving?: boolean;
  /** The company logo; the initials show without one. */
  logoUrl?: string;
  logoFallback: string;
  /** A new logo was chosen; the container uploads it and saves. */
  onLogoFile: (file: File) => void;
}

export const US_STATES = [
  { code: 'AL', name: 'Alabama' },
  { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' },
  { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' },
  { code: 'DE', name: 'Delaware' },
  { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' },
  { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' },
  { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' },
  { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' },
  { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' },
  { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' },
  { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' },
  { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' },
  { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' },
  { code: 'WY', name: 'Wyoming' },
];

const STATE_OPTIONS = US_STATES.map((state) => ({
  value: state.code,
  label: `${state.name} (${state.code})`,
}));

/**
 * The "Company Information" card on Application Settings: the logo, which
 * is also a file picker, then name, support email and phone, the street
 * address, and city / state / zip on one row, with Save Changes across the
 * bottom.
 *
 *   const form = useZodForm(companyInfoSchema, { defaultValues: company });
 *   <CompanyInfoForm form={form} onSubmit={save} saving={isPending}
 *     logoUrl={logo} logoFallback="BT" onLogoFile={upload} />
 */
export function CompanyInfoForm({
  form,
  onSubmit,
  saving = false,
  logoUrl,
  logoFallback,
  onLogoFile,
}: CompanyInfoFormProps) {
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Company Information</CardTitle>
        <CardDescription>Basic information about your company.</CardDescription>
      </CardHeader>
      <CardContent>
        <Form form={form} gap={4} onSubmit={onSubmit}>
          <Stack gap={2}>
            <Stack
              gap={0}
              onClick={() => fileInputRef.current?.click()}
              className="relative w-20 h-20 cursor-pointer"
            >
              <Avatar className="w-20 h-20">
                <AvatarImage src={logoUrl} alt="Company Logo" />
                <AvatarFallback>{logoFallback}</AvatarFallback>
              </Avatar>
              <IconTile
                icon={Pencil}
                label="Change logo"
                shape="circle"
                tone="active"
                size={7}
                className="pointer-events-none absolute bottom-2 right-2"
              />
              <Input
                type="file"
                className="hidden"
                aria-label="Choose company logo"
                ref={fileInputRef}
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (file) onLogoFile(file);
                }}
              />
            </Stack>
            <FormField form={form} name="name" label="Company Name">
              {(field) => <Input {...field} />}
            </FormField>
          </Stack>

          <FormLayout columns={2} gap="comfortable">
            <FormField form={form} name="supportEmail" label="Support Email">
              {(field) => <Input type="email" {...field} />}
            </FormField>
            <FormField form={form} name="companyPhone" label="Company Phone">
              {(field) => <Input {...field} />}
            </FormField>
          </FormLayout>

          <FormField form={form} name="streetAddress" label="Street Address">
            {(field) => <Input {...field} />}
          </FormField>

          <Grid cols={{ base: 1, md: 3 }} gap={4}>
            <FormField form={form} name="city" label="City">
              {(field) => <Input {...field} />}
            </FormField>
            <FormField form={form} name="state" label="State">
              {(field) => (
                <Select
                  options={STATE_OPTIONS}
                  placeholder="Select a state"
                  value={field.value || undefined}
                  onValueChange={field.onChange}
                />
              )}
            </FormField>
            <FormField form={form} name="zipCode" label="Zip Code">
              {(field) => <Input {...field} />}
            </FormField>
          </Grid>

          <Button type="submit" fullWidth disabled={saving}>
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Form>
      </CardContent>
    </Card>
  );
}
